import { useMemo } from 'react';
import { isNonEmptyArray } from '../../../utils';
import { createClientSideFilter, type ClientSideFilterRule } from './createClientSideFilter';

/**
 * Hook that creates a client-side filter function based on filter state and rules.
 *
 * Only creates the filter function when rules are provided. When no rules are provided,
 * returns an empty object (so it can be safely spread into return values).
 *
 * @template TFilter - The type of the filter object
 * @param filter - The current filter state
 * @param rules - Array of filter rules to apply (optional)
 * @returns An object with a `clientSideFilter` property if rules are provided, or an empty object otherwise
 *
 * @example
 * ```ts
 * const clientSideFilterProps = useClientSideFilter({
 *   filter,
 *   rules,
 * });
 *
 * // Use in return value - can always spread it
 * return {
 *   ...clientSideFilterProps,
 * };
 * ```
 */
export function useClientSideFilter<TFilter extends Record<string, any>>({
  filter,
  rules,
}: {
  filter: TFilter;
  rules?: ClientSideFilterRule<TFilter>[];
}) {
  return useMemo(() => {
    // If no rules provided, return empty object (will not add anything when spread)
    if (!isNonEmptyArray(rules)) {
      return {};
    }

    // Create the filter function based on current filter state and rules
    const filterFunction = createClientSideFilter({
      filter,
      rules,
    });

    // Return an object with the filter function that can be spread into return value
    return {
      /**
       * Client-side filter function to apply to data rows.
       * Returns true if the row should be included, false if excluded.
       * @param row - Row object containing the data to filter
       */
      clientSideFilter: (row: { data: any | null | undefined }): boolean => {
        const data = row.data;
        if (!data) {
          return false;
        }
        return filterFunction(row);
      },
    };
  }, [filter, rules]);
}
